import * as React from "react";
import { useContext } from "react";
import styled, { css, useTheme } from "styled-components";
import get from "lodash/get";
import MessageSummaryTemplateContext from "search/contexts/MessageSummaryTemplateContext";
import indicatorStatusColor from "security-content/indicatorStatusColor";
import useLicenseCheck from "license/useLicenseCheck";
import { parseIfArrayValue } from "search/messageFieldValueHelper";
const indicatorStatusFields = ["info", "primary", "success", "warning", "danger", "default", "gray"];
const indicatorAttributes = ["indicator", "defaultIndicator", ...indicatorStatusFields];
const parseArray = (value) => {
  const parsedArrayValue = parseIfArrayValue(value);
  return parsedArrayValue != null ? parsedArrayValue : [];
};
const removeMultipleSpaces = (input) => input.replace(/  +/g, " ");
const replaceFieldPlaceholder = (input, replaceFn) => removeMultipleSpaces(input.replace(/{(\w+)}/g, replaceFn));
const messageEventTypeCategories = (eventTypeCategories, messageFields) => {
  const { gim_event_subcategory: eventCategories } = messageFields;
  const eventCategoriesList = parseArray(eventCategories);
  if (eventCategoriesList.length === 0) {
    return [];
  }
  return eventCategoriesList.flatMap((categoryName) => get(eventTypeCategories, categoryName)).filter((eventCategory) => !!eventCategory);
};
const messageSummary = (eventTypeCategory, messageFields) => {
  if (!eventTypeCategory) {
    return void 0;
  }
  const formatString = eventTypeCategory.summary;
  if (!formatString) {
    return void 0;
  }
  return {
    formatString: replaceFieldPlaceholder(
      formatString,
      (fieldNamePlaceholder, fieldName) => messageFields[fieldName] !== void 0 ? fieldNamePlaceholder : ""
    ),
    summary: replaceFieldPlaceholder(
      formatString,
      (_fieldNamePlaceholder, fieldName) => messageFields[fieldName] !== void 0 ? messageFields[fieldName] : ""
    )
  };
};
const messageIndicatorStatus = (eventTypeCategory, messageFields) => {
  var _a;
  const indicatorTemplate = (_a = eventTypeCategory.indicatorTemplate) != null ? _a : {};
  const indicator = Object.entries(eventTypeCategory).filter(([key]) => indicatorAttributes.includes(key)).reduce(
    (prev, [key, value]) => ({
      ...prev,
      [key]: value
    }),
    indicatorTemplate
  );
  if (!indicator.indicator) {
    return indicator.defaultIndicator;
  }
  const fieldValue = messageFields[indicator.indicator];
  if (!fieldValue) {
    return void 0;
  }
  const matchingStatus = Object.entries(indicator).filter(([key]) => indicatorStatusFields.includes(key)).filter(([, matchingValues]) => Array.isArray(matchingValues) && matchingValues.includes(fieldValue)).map(([key]) => key);
  if (matchingStatus.length > 1) {
    console.warn(`Found more than one indicator status for field value ${fieldValue}.`);
  }
  if (!Array.isArray(matchingStatus) || !matchingStatus.length) {
    return !eventTypeCategory.defaultIndicator ? indicatorTemplate.defaultIndicator : eventTypeCategory.defaultIndicator;
  }
  return matchingStatus[0];
};
const messageSummaries = (eventTypeCats, messageFields, theme) => eventTypeCats.map((eventTypeCat) => {
  const summary = messageSummary(eventTypeCat, messageFields);
  if (!summary) {
    return void 0;
  }
  const indicatorStatus = messageIndicatorStatus(eventTypeCat, messageFields);
  const summaryColor = indicatorStatusColor(indicatorStatus, theme);
  return {
    summary,
    summaryColor
  };
}).filter((eventTypeCat) => !!eventTypeCat);
const Container = styled.div(
  ({ $color, $hideOverflow }) => css`
    color: ${$color};
    white-space: pre-line;

    &:not(:last-child) {
      margin-bottom: 3px;
    }

    ${$hideOverflow && css`
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      word-wrap: break-word;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    `}
  `
);
const MessageSummary = ({
  config = { showSummary: true },
  renderMessageRow,
  hideOverflow = false,
  messageFields
}) => {
  const theme = useTheme();
  const {
    security: { isValid: isValidSecurityLicense }
  } = useLicenseCheck();
  const contextValue = useContext(MessageSummaryTemplateContext);
  const { isLoading, isError, eventTypeCategories } = contextValue != null ? contextValue : {};
  if (!config.showSummary || !isValidSecurityLicense || isError || isLoading) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, renderMessageRow());
  }
  const eventTypeCats = messageEventTypeCategories(eventTypeCategories, messageFields);
  const summaries = messageSummaries(eventTypeCats, messageFields, theme);
  if (summaries.length === 0) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, renderMessageRow());
  }
  return summaries.map(({ summary: { formatString, summary }, summaryColor }) => /* @__PURE__ */ React.createElement(
    Container,
    {
      $color: summaryColor,
      $hideOverflow: hideOverflow,
      title: hideOverflow ? summary : formatString,
      key: formatString
    },
    summary
  ));
};
export default MessageSummary;
